import { service } from '@ember/service';
import Component from '@glimmer/component';

import { CMS_LANGUAGES } from 'umg-ember-backend/utils/cms-languages';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class RecordDetailFormsComponent extends Component {
  // Services
  @service flashMessages;
  @service history;
  @service intl;
  @service permission;
  @service recordRelationships;
  @service recordVariants;
  @service store;

  // Defaults
  cmsStates = [1, 2, 3, 5];

  countries = [
    {
      value: 'de',
      label: 'Deutschland',
    },
    {
      value: 'at',
      label: 'Österreich',
    },
    {
      value: 'ch',
      label: 'Schweiz',
    },
  ];

  languages = CMS_LANGUAGES;

  // Getter and setter
  get cmsStateOptions() {
    let scope = 'forms.defaults.options.cmsState';

    return this.cmsStates.map((type) => {
      return {
        value: type,
        label: this.intl.t(`${scope}.${type}`),
        disabled:
          !this.permission?.user?.get('isAdmin') && [6, 7].includes(type),
      };
    });
  }

  get isParent() {
    return this.recordVariants.isParent(this.args.model);
  }

  get isVariant() {
    return this.recordVariants.isVariant(this.args.model);
  }

  get isChannelVariant() {
    return this.recordVariants.isChannelVariant(this.args.model);
  }

  get variantParentId() {
    return this.recordVariants.variantParentId(this.args.model);
  }

  get availableLanguages() {
    let isNew = this.args.model.isNew;
    let isVariant = this.isVariant;
    let isChannelVariant = this.isChannelVariant;
    let languages = this.languages;
    let recordType = getRecordType(this.args.model);
    let variants = this.recordVariants.getVariants(this.args.model);

    // We return all languages if
    // - the record is not new
    // - the record is the parent
    // - the promise is not isFulfilled
    if (!isNew || this.isParent || !variants.isFulfilled) {
      return languages;
    }

    // If the record is a channel variant we filter all variants for the channel
    if (isChannelVariant) {
      variants = variants.filter((variant) => {
        return (
          variant.get('variantChannel.id') ===
          this.args.model.get('variantChannel.id')
        );
      });
    }

    // If the record is not a channel variant we filter out all channel variants
    if (!isChannelVariant) {
      variants = variants.filter(
        (variant) => !variant.get('variantChannel.id'),
      );

      // If it's a variant we add the parent
      if (isVariant) {
        let parentModel = this.store.peekRecord({
          type: recordType,
          id: String(this.variantParentId),
        });
        variants.push(parentModel);
      }
    }

    // Get languageIds
    let assigned = variants.map((variant) => variant.cmsLanguageId);

    return languages.filter((lang) => !assigned.includes(lang.value));
  }

  // Actions
  newRecord = async (
    model,
    relationKind,
    relationType,
    relationalField,
    filters,
  ) => {
    let newRecord = await this.store.createRecord(relationType, {
      position: model[relationalField].length,
    });

    if (filters) {
      for (let key of Object.keys(filters)) {
        newRecord[key] = filters[key];
      }
    }

    if (relationKind === 'belongsTo') {
      model[relationalField] = newRecord;
    } else {
      let existingRecords = await model[relationalField];
      model[relationalField] = [...existingRecords, newRecord];
    }
  };

  deleteRelation = async (record, model, relationKind, relationalField) => {
    let ok = await this.history.confirmAction('delete', record);

    if (!ok) {
      return;
    }

    try {
      let loadedRecord = await record;

      if (loadedRecord) {
        this.recordRelationships.watch(model, relationalField, async () => {
          try {
            await loadedRecord.destroyRecord();

            if (relationKind === 'belongsTo') {
              model[relationalField] = null;
            } else {
              let existingRecords = await model[relationalField];
              let indexOfRecord = existingRecords.indexOf(loadedRecord);

              if (indexOfRecord !== -1) {
                existingRecords.splice(indexOfRecord, 1);
              }
            }
          } catch (error) {
            loadedRecord.rollbackAttributes();
            this.flashMessages.serverError(error);
          }
        });
      }
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };
}
